import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Col, Row } from 'react-bootstrap';

// props:
// - visible
// - vehicle
// - onHide
// - onVehicleSaved
function VehicleEditDialog(props) {

  // #region vars and consts

  const [isNew, setIsNew] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState(false);
  const [active, setActive] = useState(true);
  const defaultValues = {
    callSign: "",
    licensePlate: "",
    description: "",
    capacityBags: 0,
    seats: 0,
    vehicleType: null
  };
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset
  } = useForm({ defaultValues });

  // #endregion

  // #region methods

  const resetForm = () => {
    reset(defaultValues);
  }

  const getVehicleTypes = () => {    
    fetch(`${process.env.REACT_APP_API_URL}/vehicleType/cmb?generic=false`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setVehicleTypes(data.responseObject);          
        } else {
          console.warn('VehicleEditDialog: getVehicleTypes: error: data:', data);
        }        
      });
  };
  
  const createVehicle = (
    callSign,
    licensePlate,
    description,
    capacityBags,
    seats,
    vehicleTypeId,
    active) => {
    fetch(`${process.env.REACT_APP_API_URL}/vehicle/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        callSign,
        licensePlate,
        description,
        capacityBags,
        seats,
        vehicleTypeId,
        active
      }),
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.log("VehicleEditDialog: createVehicle: success");
        props.onVehicleSaved();
        reset();
      } else {
        console.log("VehicleEditDialog: createVehicle: error");
      }
    });
  };

  const updateVehicle = (
    vehicleId,
    callSign,
    licensePlate,
    description,
    capacityBags,
    seats,
    vehicleTypeId,
    active) => {
    fetch(`${process.env.REACT_APP_API_URL}/vehicle/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        vehicleId,
        callSign,
        licensePlate,
        description,
        capacityBags,
        seats,
        vehicleTypeId,
        active
      }),
    }).then(response => response.json()).then(data => {
      if (data.responseCode === 'OK') {
        console.log("VehicleEditDialog: updateVehicle: success");
        props.onVehicleSaved();
        reset();
      } else {
        console.log("VehicleEditDialog: updateVehicle: error");
      }
    });
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && (
      <small className="p-error">
        {errors[name].message}
      </small>
    )
  };

  // #endregion

  // #region events

  const onDialogHide = () => {
    if (props.onHide) props.onHide();
  };

  const onFormSubmit = (data) => {
    if (isNew) {
      createVehicle(
        data.callSign,
        data.licensePlate,
        data.description,
        data.capacityBags,
        data.seats,
        data.vehicleType.vehicleTypeId,
        active
      );
    } else {
      updateVehicle(
        props.vehicle.vehicleId,
        data.callSign,
        data.licensePlate,
        data.description,
        data.capacityBags,
        data.seats,
        data.vehicleType.vehicleTypeId,
        active);
    }
  };

  // #endregion

  // #region templates

  const formTemplate = (
    <>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="p-fluid">
        <Row>
          <Col md={6} className='pt-1'>
            <label
              htmlFor="callSign"
              className={classNames({ 'p-error': errors.callSign })}>
              No Externo*
            </label>
            <Controller
              name="callSign"
              control={control}
              rules={{ required: 'El no. externo es requerido.'}}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  maxLength={255}
                  autoFocus
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('callSign')}
          </Col>
          <Col md={6} className='pt-1'>
            <label
              htmlFor="licensePlate"
              className={classNames({ 'p-error': errors.licensePlate })}>
              Placa
            </label>
            <Controller
              name="licensePlate"
              control={control}
              rules={{ required: 'La placa requerido.' }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  maxLength={255}
                  autoFocus
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('licensePlate')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="description"
              className={classNames({ 'p-error': errors.description })}>
              Descripción
            </label>
            <Controller
              name="description"
              control={control}
              rules={{ required: 'La descripción requerido.' }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  maxLength={255}
                  autoFocus
                  className={classNames({ 'p-invalid': fieldState.invalid })} />
              )} />
            {getFormErrorMessage('description')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1' sm={6}>
            <label
              htmlFor="capacityBags"
              className={classNames({ 'p-error': errors.capacityBags })}>
              Capacidad de maletas*
            </label>
            <Controller
              name="capacityBags"
              control={control}
              rules={{ required: 'La capacidad de maletas es requerida.', min: 0, max: 1000 }}
              render={({ field, fieldState }) => (
                <InputNumber
                  id={field.name}
                  ref={field.ref}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(e) => field.onChange(e)}                                 
                  inputClassName={classNames({ 'p-invalid': fieldState.error })} />
              )} />            
            {getFormErrorMessage('capacityBags')}
          </Col>
          <Col className='pt-1' sm={6}>
            <label
              htmlFor="seats"
              className={classNames({ 'p-error': errors.seats })}>
              Asientos*
            </label>
            <Controller
              name="seats"
              control={control}
              rules={{ required: 'Los asientos son requeridos.', min: 0, max: 1000 }}
              render={({ field, fieldState }) => (
                <InputNumber
                  id={field.name}
                  ref={field.ref}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(e) => field.onChange(e)}
                  inputClassName={classNames({ 'p-invalid': fieldState.error })} />
              )} />
            {getFormErrorMessage('seats')}
          </Col>
        </Row>        
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="vehicleType"
              className={classNames({ 'p-error': errors.vehicleType })}>
              Tipo de vehículo*
            </label>
            <Controller
              name="vehicleType"
              control={control}
              rules={{ required: 'El tipo de vehículo es requerido.' }}
              render={({ field }) => (
                <Dropdown
                  id={field.name}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  options={vehicleTypes}
                  optionLabel="description" />
              )} />
            {getFormErrorMessage('vehicleType')}
          </Col>
        </Row>
        <Row>
          <Col className='pt-1'>
            <label
              htmlFor="active">              
              Activo
            </label>
            <br></br>
            <InputSwitch checked={active} onChange={(e) => setActive(e.value)} />
          </Col>
        </Row>
        <Button
          type="submit"
          label="Guardar"
          className="p-button-raised p-button-sm mt-4" />
      </form>
    </>
  );

  const dialogTemplate = (
    <>
      <Dialog
        header=""
        visible={props.visible}
        onHide={onDialogHide}
        breakpoints={{ '960px': '75vw' }}
        style={{ width: '50vw' }}>
        <h1>Vehículo</h1>
        {formTemplate}
      </Dialog>
    </>
  );

  // #endregion

  useEffect(() => {
    resetForm();
    getVehicleTypes();
    if (props.vehicle) {
      setIsNew(false);
      reset(props.vehicle);
      setActive(props.vehicle.active);
    } else {
      setIsNew(true);
    }
  }, [props.visible, props.vehicle]);

  return (
    <>
      <article>
        {dialogTemplate}
      </article>
    </>
  );
}

export default VehicleEditDialog;